import React, { useState, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { PackageInquiryModal } from './PackageInquiryModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const PricingPage = () => {
  const { language } = useLanguage();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = (key) => getTranslation(language, key);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch packages
        const packagesResponse = await fetch(`${API}/packages`);
        if (packagesResponse.ok) {
          const data = await packagesResponse.json();
          if (data.length > 0) {
            setPackages(data);
          } else {
            setPackages(getDefaultPackages());
          }
        }
        
        // Fetch additional services
        const servicesResponse = await fetch(`${API}/additional-services`);
        if (servicesResponse.ok) {
          const servicesData = await servicesResponse.json();
          if (servicesData.length > 0) {
            setAdditionalServices(servicesData);
          } else {
            // Fallback to default
            setAdditionalServices(getDefaultAdditionalServices());
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setPackages(getDefaultPackages());
        setAdditionalServices(getDefaultAdditionalServices());
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const getDefaultAdditionalServices = () => [
    { id: '1', name: 'Presentation Website', price: '+2000 €' },
    { id: '2', name: 'Website eCommerce', price: '+3000 €' },
    { id: '3', name: 'Motion Website Experience', price: '+5000 €' },
    { id: '4', name: 'Brandbook', price: '+2000 €' },
    { id: '5', name: 'Writing and publishing articles on 20 news websites', price: '+500 €' }
  ];

  const getDefaultPackages = () => [
    {
      id: 'essential-standard',
      name: 'Essential Standard',
      price: '2500',
      category: 'essential',
      features: [
        { text: '18 PHOTOS', included: true },
        { text: '10 REELS', included: true },
        { text: 'Analytics & Reporting', included: true },
        { text: '24/7 Client Service', included: true },
        { text: 'Photo Graphic Design', included: true }
      ],
      popular: false,
      premium: false
    },
    {
      id: 'essential-gold',
      name: 'Essential Gold',
      price: '3000',
      category: 'essential',
      features: [
        { text: '18 PHOTOS', included: true },
        { text: '12 REELS', included: true },
        { text: 'Analytics & Reporting', included: true },
        { text: '24/7 Client Service', included: true },
        { text: 'Photo Graphic Design', included: true }
      ],
      popular: true,
      premium: false
    },
    {
      id: 'essential-premium',
      name: 'Essential Premium',
      price: '5000',
      category: 'essential',
      features: [
        { text: '18 PHOTOS', included: true },
        { text: '20 REELS', included: true },
        { text: 'Analytics & Reporting', included: true },
        { text: '24/7 Client Service', included: true },
        { text: 'Photo Graphic Design', included: true }
      ],
      popular: false,
      premium: true
    },
    {
      id: 'prestige-standard',
      name: 'Prestige Standard',
      price: '4500',
      category: 'prestige',
      features: [
        { text: '18 PHOTOS', included: true },
        { text: '8 REELS', included: true },
        { text: 'SM Marketing Strategy', included: true },
        { text: 'SM Management & Support', included: true },
        { text: '24/7 Client Service', included: true },
        { text: 'Photo Graphic Design', included: true },
        { text: 'Analytics & Reporting', included: true }
      ],
      popular: false,
      premium: false
    },
    {
      id: 'prestige-gold',
      name: 'Prestige Gold',
      price: '6000',
      category: 'prestige',
      features: [
        { text: '18 PHOTOS', included: true },
        { text: '12 REELS', included: true },
        { text: 'SM Marketing Strategy', included: true },
        { text: 'SM Management & Support', included: true },
        { text: '24/7 Client Service', included: true },
        { text: 'Photo Graphic Design', included: true },
        { text: 'Analytics & Reporting', included: true }
      ],
      popular: true,
      premium: false
    },
    {
      id: 'prestige-premium',
      name: 'Prestige Premium',
      price: '10000',
      category: 'prestige',
      features: [
        { text: '18 PHOTOS', included: true },
        { text: '20 REELS', included: true },
        { text: 'SM Marketing Strategy', included: true },
        { text: 'SM Management & Support', included: true },
        { text: '24/7 Client Service', included: true },
        { text: 'Photo Graphic Design', included: true },
        { text: 'Analytics & Reporting', included: true },
        { text: '360 Marketing Strategy', included: true },
        { text: 'Influencer Marketing', included: true },
        { text: 'Campaign Management', included: true },
        { text: 'Brand Activation & Event Support', included: true }
      ],
      popular: false,
      premium: true
    }
  ];

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
  };

  const scrollToPackages = () => {
    const element = document.getElementById('packages');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const essentialPackages = packages.filter(p => p.category === 'essential');
  const prestigePackages = packages.filter(p => p.category === 'prestige');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
        {/* Animated Stars Background */}
        <div className="absolute inset-0">
          <div className="stars-layer stars-small"></div>
          <div className="stars-layer stars-medium"></div>
          <div className="stars-layer stars-large"></div>
        </div>

        {/* Background Effects */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/40 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/40 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Floating Geometric Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-20 h-20 border border-pink-500/20 rounded-lg animate-float-rotate" style={{ animationDuration: '15s' }}></div>
          <div className="absolute top-40 right-20 w-16 h-16 border border-blue-500/20 rounded-full animate-float-rotate" style={{ animationDuration: '20s', animationDelay: '2s' }}></div>
          <div className="absolute bottom-40 left-1/4 w-12 h-12 bg-gradient-to-br from-pink-500/10 to-transparent rounded-lg animate-float-rotate" style={{ animationDuration: '18s', animationDelay: '1s' }}></div>
          <div className="absolute top-1/3 right-1/4 w-24 h-24 border border-pink-500/10 rounded-full animate-float-rotate" style={{ animationDuration: '25s', animationDelay: '3s' }}></div>
          <div className="absolute bottom-1/3 right-10 w-14 h-14 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full animate-float-rotate" style={{ animationDuration: '22s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="space-y-8">
            {/* Badge with entrance animation */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900/50 backdrop-blur-md border border-pink-500/30 rounded-full mb-8 animate-fade-in-down"
              style={{ animationDuration: '0.8s' }}
            >
              <span className="text-sm text-gray-300 font-medium">Pricing Packages</span>
            </div>

            {/* Title with staggered animation */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-tight overflow-hidden">
              <span 
                className="inline-block animate-slide-up-fade"
                style={{ animationDuration: '0.8s', animationDelay: '0.2s', animationFillMode: 'both' }}
              >
                Choose Your
              </span>
              <br />
              <span 
                className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-pink-400 to-blue-600 animate-slide-up-fade animate-gradient"
                style={{ animationDuration: '0.8s', animationDelay: '0.4s', animationFillMode: 'both' }}
              >
                Perfect Package
              </span>
            </h1>

            {/* Description with fade animation */}
            <p 
              className="text-xl text-gray-400 max-w-2xl mx-auto animate-fade-in"
              style={{ animationDuration: '0.8s', animationDelay: '0.6s', animationFillMode: 'both' }}
            >
              From essential to premium solutions, we have the perfect package to elevate your brand
            </p>

            {/* Button with scale animation */}
            <div 
              className="pt-8 animate-scale-in"
              style={{ animationDuration: '0.6s', animationDelay: '0.8s', animationFillMode: 'both' }}
            >
              <Button
                onClick={scrollToPackages}
                size="lg"
                className="group relative bg-gradient-to-r from-blue-600 to-pink-600 hover:from-blue-700 hover:to-pink-700 text-white px-10 py-7 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-pink-500/50 transition-all duration-500 transform hover:scale-110 overflow-hidden"
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative z-10 flex items-center gap-2">
                  View Packages
                  <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </span>
              </Button>
            </div>
          </div>

          {/* Scroll Indicator */}
          <button
            onClick={scrollToPackages}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce-enhanced group"
            style={{ animationDelay: '1.2s' }}
          >
            <div className="flex flex-col items-center text-gray-400 hover:text-pink-400 transition-colors duration-300">
              <span className="text-sm mb-2 uppercase tracking-wider">Our Packages</span>
              <ChevronDown size={32} />
            </div>
          </button>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white text-center mb-12">Objectives</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700 hover:border-pink-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <p className="text-gray-300">
                  Positioning your brand as a distinct presence in the market, standing out through professionalism and authenticity.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700 hover:border-pink-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <p className="text-gray-300">
                  Developing and implementing a customized social media strategy, designed to highlight your unique values.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700 hover:border-pink-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <p className="text-gray-300">
                  Creating original and captivating content that reflects your vision, attracting ideal clients.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Essential Packages */}
      {essentialPackages.length > 0 && (
        <section id="packages" className="py-20 bg-slate-950">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Essential Pack</h2>
              <p className="text-gray-400 max-w-3xl mx-auto">
                We write the script, film and edit professionally. You receive finished clips with correct colors, clear sound and smooth transitions.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {essentialPackages.map((pkg) => (
                <PricingCard key={pkg.id} package={pkg} onSelect={handleSelectPackage} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Prestige Packages */}
      {prestigePackages.length > 0 && (
        <section className="py-20 bg-slate-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Prestige Pack</h2>
              <p className="text-gray-400 max-w-3xl mx-auto">
                We add motion graphics, After Effects visual effects, custom transitions and advanced color grading for content that stands out.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {prestigePackages.map((pkg) => (
                <PricingCard key={pkg.id} package={pkg} onSelect={handleSelectPackage} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Additional Services */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Additional Services</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {additionalServices.map((service, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:border-pink-500/50 transition-all duration-300">
                <CardContent className="p-6 flex justify-between items-center">
                  <span className="text-gray-300">{service.name}</span>
                  <span className="text-pink-400 font-bold">{service.price}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Package Inquiry Modal */}
      {selectedPackage && (
        <PackageInquiryModal
          package={selectedPackage}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

const PricingCard = ({ package: pkg, onSelect }) => {
  return (
    <Card 
      className={`relative bg-slate-800/80 backdrop-blur-sm border-2 transition-all duration-500 transform hover:scale-105 ${
        pkg.popular 
          ? 'border-pink-500 shadow-2xl shadow-pink-500/20' 
          : pkg.premium
          ? 'border-blue-500 shadow-2xl shadow-blue-500/20'
          : 'border-slate-700 hover:border-pink-500/50'
      }`}
      data-testid={`pricing-card-${pkg.id}`}
    >
      {pkg.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Popular
          </span>
        </div>
      )}
      {pkg.premium && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Premium
          </span>
        </div>
      )}

      <CardHeader className="text-center pb-8 pt-8">
        <h3 className="text-2xl font-bold text-white mb-4">{pkg.name}</h3>
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400">
            {pkg.price}
          </span>
          <span className="text-gray-400">€</span>
        </div>
        <p className="text-sm text-gray-500 mt-2">+ TVA</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {pkg.features && pkg.features.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <Check className="text-pink-400 mt-1 flex-shrink-0" size={20} />
            <span className="text-gray-300">{feature.text}</span>
          </div>
        ))}

        <Button
          onClick={() => onSelect(pkg)}
          className="w-full mt-6 bg-gradient-to-r from-blue-600 to-pink-600 hover:from-blue-700 hover:to-pink-700 text-white py-6 text-lg font-semibold transition-all duration-300"
          data-testid={`select-package-${pkg.id}`}
        >
          Select Package
        </Button>
      </CardContent>
    </Card>
  );
};
